import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Building2, Shield, Lock, Server, Eye, FileCheck, 
  CheckCircle, Globe, Database, Key, Users, AlertTriangle,
  ArrowLeft, ArrowRight, Fingerprint, ShieldCheck, Cloud,
  MapPin, Clock, Zap, Award
} from 'lucide-react';

const Security = () => {
  const certifications = [
    { name: 'SOC 2 Type II', status: 'AWS Certified', icon: Award },
    { name: 'PCI-DSS', status: 'Compliant Infrastructure', icon: FileCheck },
    { name: 'ISO 27001', status: 'AWS Certified', icon: Shield },
    { name: 'RBI Data Localization', status: 'Compliant', icon: MapPin },
  ];

  const securityFeatures = [
    {
      category: 'Data Security',
      icon: Database,
      color: 'blue',
      items: [
        { title: 'AES-256 Encryption', desc: 'All data encrypted at rest using bank-grade encryption' },
        { title: 'TLS 1.3', desc: 'All data in transit protected with latest encryption protocols' },
        { title: 'Key Management', desc: 'AWS KMS for secure key storage and rotation' },
        { title: 'Data Isolation', desc: 'Complete tenant isolation in multi-tenant architecture' },
      ]
    },
    {
      category: 'Infrastructure Security',
      icon: Server,
      color: 'green',
      items: [
        { title: 'AWS Mumbai Region', desc: 'Hosted exclusively in India for RBI compliance' },
        { title: 'DDoS Protection', desc: 'AWS Shield protects against distributed attacks' },
        { title: 'Web Application Firewall', desc: 'AWS WAF blocks malicious requests' },
        { title: '99.99% Uptime SLA', desc: 'Multi-AZ deployment for high availability' },
      ]
    },
    {
      category: 'Access Security',
      icon: Key,
      color: 'purple',
      items: [
        { title: 'Two-Factor Authentication', desc: 'TOTP-based 2FA for all user accounts' },
        { title: 'Role-Based Access', desc: 'Granular permissions per user role' },
        { title: 'Session Management', desc: 'Automatic timeout and secure session handling' },
        { title: 'Rate Limiting', desc: 'Protection against brute force attacks' },
      ]
    },
    {
      category: 'Audit & Compliance',
      icon: Eye,
      color: 'orange',
      items: [
        { title: 'Complete Audit Trail', desc: 'Every action logged with timestamp and user' },
        { title: 'Immutable Logs', desc: 'Tamper-proof logging for compliance' },
        { title: 'Real-time Monitoring', desc: '24/7 automated security monitoring' },
        { title: 'Incident Response', desc: 'Defined procedures for security events' },
      ]
    },
  ];

  const trustedBy = [
    'Razorpay', 'Paytm', 'PhonePe', 'CRED', 'Zerodha', 'Groww'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Navbar */}
      <nav className="px-6 py-4 flex items-center justify-between max-w-7xl mx-auto">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center shadow-lg">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <span className="text-xl font-bold text-white font-['Outfit']">FinOrchest</span>
        </Link>
        <div className="flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
          <Link to="/register">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white">
              Get Started
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-6 pt-16 pb-12">
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/20 mb-6">
            <ShieldCheck className="w-4 h-4 text-green-400" />
            <span className="text-sm text-green-400 font-medium">Bank-Grade Security</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 font-['Outfit']">
            Your Data is
            <span className="text-blue-400"> Protected</span>
          </h1>
          
          <p className="text-xl text-slate-400 mb-8 leading-relaxed">
            FinOrchest is built on the same infrastructure trusted by India's leading 
            fintech companies. We take security seriously so you can focus on your business.
          </p>
        </div>

        {/* Trust Badges - Including India Data Residency */}
        <div className="flex flex-wrap justify-center gap-4 mb-16">
          {certifications.map((cert, index) => (
            <div 
              key={index}
              className="flex items-center gap-3 px-5 py-3 rounded-xl bg-slate-800/50 border border-slate-700"
            >
              <cert.icon className="w-5 h-5 text-blue-400" />
              <div>
                <p className="text-sm font-medium text-white">{cert.name}</p>
                <p className="text-xs text-slate-400">{cert.status}</p>
              </div>
            </div>
          ))}
          {/* India Data Residency Badge */}
          <div className="flex items-center gap-3 px-5 py-3 rounded-xl bg-orange-500/10 border border-orange-500/30">
            <Cloud className="w-5 h-5 text-orange-400" />
            <div>
              <p className="text-sm font-medium text-white">🇮🇳 India Data Residency</p>
              <p className="text-xs text-orange-400">AWS Mumbai Region</p>
            </div>
          </div>
        </div>
      </div>

      {/* Security Features Grid */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        <h2 className="text-3xl font-bold text-white text-center mb-4 font-['Outfit']">
          Comprehensive Security
        </h2>
        <p className="text-slate-400 text-center mb-12 max-w-2xl mx-auto">
          Multiple layers of protection ensure your financial data remains secure at all times
        </p>

        <div className="grid md:grid-cols-2 gap-8">
          {securityFeatures.map((section, sectionIndex) => (
            <Card key={sectionIndex} className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className={`w-12 h-12 rounded-xl bg-${section.color}-500/10 flex items-center justify-center`}>
                    <section.icon className={`w-6 h-6 text-${section.color}-400`} />
                  </div>
                  <h3 className="text-xl font-semibold text-white">{section.category}</h3>
                </div>
                <div className="space-y-4">
                  {section.items.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-white">{item.title}</p>
                        <p className="text-sm text-slate-400">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* RBI Compliance Section */}
      <div className="bg-slate-800/30 border-y border-slate-700 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-blue-500/10 text-blue-400 mb-4">RBI Compliant</Badge>
              <h2 className="text-3xl font-bold text-white mb-4 font-['Outfit']">
                100% India Data Residency
              </h2>
              <p className="text-slate-400 mb-6 leading-relaxed">
                In compliance with RBI's data localization mandate, all your payment data, 
                transaction records, and personal information are stored exclusively in 
                India. We never transfer your data outside Indian borders.
              </p>
              <div className="space-y-3">
                {[
                  'All servers located in AWS Mumbai data center',
                  'No data replication to foreign regions',
                  'Compliant with RBI circular on Storage of Payment System Data',
                  'Regular compliance audits and certifications',
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span className="text-slate-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-3xl blur-3xl" />
              <div className="relative bg-slate-800 rounded-2xl border border-slate-700 p-8">
                <div className="flex items-center gap-4 mb-6">
                  <MapPin className="w-8 h-8 text-blue-400" />
                  <div>
                    <p className="text-lg font-semibold text-white">Data Location</p>
                    <p className="text-slate-400">Mumbai, Maharashtra, India</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 rounded-lg bg-slate-700/50">
                    <span className="text-slate-300">Primary Data Center</span>
                    <Badge className="bg-green-500/10 text-green-400">AWS ap-south-1a</Badge>
                  </div>
                  <div className="flex justify-between items-center p-3 rounded-lg bg-slate-700/50">
                    <span className="text-slate-300">Backup Data Center</span>
                    <Badge className="bg-green-500/10 text-green-400">AWS ap-south-1b</Badge>
                  </div>
                  <div className="flex justify-between items-center p-3 rounded-lg bg-slate-700/50">
                    <span className="text-slate-300">Disaster Recovery</span>
                    <Badge className="bg-green-500/10 text-green-400">AWS ap-south-1c</Badge>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Security Practices */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        <h2 className="text-3xl font-bold text-white text-center mb-4 font-['Outfit']">
          Our Security Practices
        </h2>
        <p className="text-slate-400 text-center mb-12 max-w-2xl mx-auto">
          We follow industry best practices to ensure continuous security
        </p>

        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              icon: Clock,
              title: 'Daily Backups',
              desc: 'Automated daily backups with 30-day retention. Point-in-time recovery available.',
              color: 'blue'
            },
            {
              icon: Zap,
              title: 'Real-time Monitoring',
              desc: 'CloudWatch alerts for anomalies. Automated incident response procedures.',
              color: 'yellow'
            },
            {
              icon: Fingerprint,
              title: 'Penetration Testing',
              desc: 'Annual third-party security assessments. Vulnerability scanning and remediation.',
              color: 'purple'
            },
            {
              icon: Users,
              title: 'Access Reviews',
              desc: 'Quarterly access reviews. Principle of least privilege enforced.',
              color: 'green'
            },
            {
              icon: Lock,
              title: 'Secrets Management',
              desc: 'AWS Secrets Manager for credentials. Automatic key rotation.',
              color: 'red'
            },
            {
              icon: AlertTriangle,
              title: 'Incident Response',
              desc: 'Documented IR procedures. <4 hour response time for critical issues.',
              color: 'orange'
            },
          ].map((practice, index) => (
            <div 
              key={index}
              className="p-6 rounded-xl bg-slate-800/50 border border-slate-700 hover:border-slate-600 transition-colors"
            >
              <div className={`w-12 h-12 rounded-xl bg-${practice.color}-500/10 flex items-center justify-center mb-4`}>
                <practice.icon className={`w-6 h-6 text-${practice.color}-400`} />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{practice.title}</h3>
              <p className="text-slate-400 text-sm">{practice.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Same Infrastructure Section */}
      <div className="bg-gradient-to-r from-blue-600/10 via-blue-600/5 to-blue-600/10 border-y border-blue-500/20 py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold text-white mb-4 font-['Outfit']">
            Trusted by India's Leading Fintech Companies
          </h2>
          <p className="text-slate-400 mb-8">
            We use the same AWS infrastructure that powers these companies
          </p>
          <div className="flex flex-wrap justify-center gap-8">
            {trustedBy.map((company, index) => (
              <div 
                key={index}
                className="px-6 py-3 rounded-lg bg-slate-800/50 border border-slate-700 text-slate-400 font-medium"
              >
                {company}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="max-w-4xl mx-auto px-6 py-20">
        <h2 className="text-3xl font-bold text-white text-center mb-12 font-['Outfit']">
          Security FAQ
        </h2>
        
        <div className="space-y-4">
          {[
            {
              q: 'Where is my data stored?',
              a: 'All your data is stored exclusively in AWS Mumbai data center (ap-south-1) in India. We comply with RBI\'s data localization requirements and never transfer data outside India.'
            },
            {
              q: 'How is my data encrypted?',
              a: 'We use AES-256 encryption for data at rest and TLS 1.3 for data in transit. Encryption keys are managed through AWS KMS with automatic rotation.'
            },
            {
              q: 'Who can access my organization\'s data?',
              a: 'Only users you authorize can access your data. We use role-based access control, and even our support team cannot access your financial data without explicit permission.'
            },
            {
              q: 'What happens if there\'s a security incident?',
              a: 'We have documented incident response procedures with <4 hour response time for critical issues. You will be notified immediately of any incident affecting your data.'
            },
            {
              q: 'Do you have compliance certifications?',
              a: 'Our infrastructure (AWS) is SOC 2 Type II, PCI-DSS, and ISO 27001 certified. We are working towards our own ISO 27001 certification.'
            },
            {
              q: 'Can I export my data?',
              a: 'Yes, you can export all your data at any time in standard formats (CSV, PDF). We believe in data portability and will never hold your data hostage.'
            },
          ].map((faq, index) => (
            <div 
              key={index}
              className="p-6 rounded-xl bg-slate-800/50 border border-slate-700"
            >
              <h3 className="text-lg font-semibold text-white mb-2">{faq.q}</h3>
              <p className="text-slate-400">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-6 pb-20">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-12 text-center">
          <h2 className="text-3xl font-bold text-white mb-4 font-['Outfit']">
            Ready to Secure Your Finances?
          </h2>
          <p className="text-blue-100 mb-8 max-w-xl mx-auto">
            Join hundreds of businesses that trust FinOrchest with their financial operations.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 gap-2">
                Start Free Trial <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-8">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <p className="text-slate-500 text-sm">© 2026 FinOrchest. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <Link to="/security" className="text-sm text-slate-400 hover:text-white">Security</Link>
            <Link to="/privacy" className="text-sm text-slate-400 hover:text-white">Privacy</Link>
            <Link to="/terms" className="text-sm text-slate-400 hover:text-white">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Security;
